// src/lib/invoicing.js
// Invoice line mathematics — #284, Session 74z.
//
// These predicates were local to PurchasingQueuePage.js until invoice
// reconciliation moved onto its own page. They live here rather than on the new
// page because #288 (74z3) puts a short-close action on BOTH surfaces: receipt
// close is the buyer's and lives in On Order, invoice close is AP's and lives on
// Invoicing. Two pages need the same answer to "is anybody still waiting on this
// line", so the rule gets one home before the second caller arrives rather than
// after.
//
// Nothing here touches the database. The mutation sequence stays with the page
// that owns it — there is exactly one caller of it, and extracting a single
// caller is not sharing, it is indirection.

// #283. A line can be billed across two invoices, so "has a recorded receipt" is
// not enough to decide it still belongs on the sheet. Without this, a line
// already paid for reappears on the next invoice and gets paid for twice.
export function itemBilledQty(it) {
  return (it?.material_request_invoice_lines || [])
    .reduce((sum, l) => sum + (l.qty_invoiced != null ? parseFloat(l.qty_invoiced) : 0), 0);
}

// What this line can still be billed for. Not the lifetime received quantity —
// on a second invoice against the same line those differ, and showing the
// lifetime figure would invite billing for it again.
export function itemOutstandingQty(it) {
  const received = it?.qty_received != null ? parseFloat(it.qty_received) : 0;
  return received - itemBilledQty(it);
}

// #279. A line with no recorded receipt is not invoiceable. This is Oracle's
// rule — quantity billed must be less than or equal to quantity received —
// expressed as a screen behaviour, and SAP and Coupa both match the invoice to
// the goods receipt the same way.
//
// The defect this closed: the single-item receive path reached 'received'
// without writing qty_received on any line, so an undelivered line appeared on
// the invoice sheet, reported the ordered quantity as received, and reconciled
// clean. The buyer paid for goods that never arrived.
export function invoiceableItems(items) {
  return (items || []).filter(it => it.qty_received != null && parseFloat(it.qty_received) > 0
    && itemOutstandingQty(it) > 0.0001);
}

// A line nobody is waiting on any more, for either reason: everything ordered
// arrived, or someone declared the balance is not coming (#288's receipt_closed).
export function itemReceiptSettled(it) {
  if (it?.receipt_closed) return true;
  return it?.qty_received != null && parseFloat(it.qty_received) >= parseFloat(it.qty_requested);
}

// The single test for "can this be invoiced", used by the page's own list, both
// detail surfaces and the card action so they cannot drift apart.
export function hasInvoiceableLines(req) {
  return invoiceableItems(req?.material_request_items || []).length > 0;
}

// A line that is finished for payment: everything receivable has been billed, or
// AP declared it settled short (#288's invoice_closed, 74z3).
export function itemInvoiceSettled(it) {
  if (it?.invoice_closed) return true;
  return itemReceiptSettled(it) && itemOutstandingQty(it) <= 0.0001;
}

// Screen 9 (#212). An item's ordered price lives in one of two places, never
// both. A catalog line carries its own ordered_price, a generated column
// computed as price_per_pack * packs_ordered. An off-catalog request keeps its
// price on the request header instead, because
// recompute_material_request_item_totals() returns early on an off-catalog row
// and would never roll a line-level price up.
//
// The itemCount === 1 guard is what makes that header fallback exact rather than
// assumed: every off-catalog request in the database is single-item, verified
// live at Session 74k9. Should a multi-item off-catalog request ever exist, this
// returns null and the row renders unpriced rather than attributing a request
// total to one of several lines.
//
// null is a real, common answer: every catalog request received before Session
// 74k4 predates per-item pricing and carries no price anywhere.
export function itemOrderedPrice(req, it, itemCount) {
  if (it?.ordered_price != null) return parseFloat(it.ordered_price);
  if (it?.is_off_catalog && itemCount === 1 && req?.ordered_price != null) return parseFloat(req.ordered_price);
  return null;
}

// #285. Terms drive the due date so Bridgette types one date rather than two.
// Free text is stored rather than an id because there is no vendor-level terms
// column yet — when one exists this list defaults from it instead of from Net 30.
export const PAYMENT_TERMS = [
  { label: 'Due on receipt', days: 0 },
  { label: 'Net 15', days: 15 },
  { label: 'Net 30', days: 30 },
  { label: 'Net 45', days: 45 },
  { label: 'Net 60', days: 60 },
];

// Both arguments and the return value are 'YYYY-MM-DD'. Built at noon rather
// than midnight for the same reason the Mark Ordered ETA field is — a midnight
// local Date crosses a day boundary under some timezone offsets.
export function dueDateFromTerms(dateStr, termsLabel) {
  const entry = PAYMENT_TERMS.find(t => t.label === termsLabel);
  if (!dateStr || !entry) return '';
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + entry.days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + mm + '-' + dd;
}

export function todayInputValue() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + mm + '-' + dd;
}
